import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { LS_REPORTING_MODE_KEY, REPORTING_MODES, REPORTING_MODES_INFO } from '@/helpers/reporting';
import { prettyDate } from '@/helpers/date';
import { APP_LINKS } from '@/helpers/links';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MyLink } from '@/components/MyLink';
import { useGetReportingModes } from '@/query/queries/reporting/useGetReportingModes';
import { useGetTakeRateStatus } from '@/query/queries/reporting/useGetTakeRateStatus';
import { useApplyReportingMode } from '@/query/queries/reporting/useApplyReportingMode';
import IconChecked from './icon_checked.svg';
import IconUnchecked from './icon_unchecked.svg';
import IconWarn from './icon_warn.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.reporting_mode;

export const ReportingModeModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = () => {
  const [selected, setSelected] = useState(null);
  const { data: modes } = useGetReportingModes();
  const { data: status } = useGetTakeRateStatus();
  const { t } = useTranslation('modal');
  const { mutateAsync: applyMode } = useApplyReportingMode();
  const { closeModal } = useModal();

  useEffect(() => {
    if (modes && status && !selected) {
      setSelected(modes.find(m => m.reporting_mode === status.reporting_mode));
    }
  }, [modes, status, selected]);

  if (!modes || !status) return null;

  const onClose = () => {
    localStorage.setItem(LS_REPORTING_MODE_KEY, '1');
    closeModal(id);
  };

  const onSubmit = async () => {
    const res = await applyMode({ reporting_mode: selected.reporting_mode });
    res && onClose();
  };

  const isFirstTime = localStorage.getItem(LS_REPORTING_MODE_KEY) !== '1';

  const isRevertTransparent =
    status.reporting_mode === REPORTING_MODES.OPAQUE &&
    status?.next_reporting_mode_from &&
    status?.next_reporting_mode === REPORTING_MODES.TRANSPARENT;

  const hasUnavailable = modes?.some(item => item.is_available === false);

  const content = (
    <div className={classnames(s.modal, 'scr_modal_grid')}>
      <div className={s.header}>
        <div className="f-h2 f-500">{t('reporting_mode_modal.title')}</div>
        <div className={s.desc}>
          {t('reporting_mode_modal.description')}{' '}
          <MyLink className={s.learn} href={APP_LINKS.loanTermsReportingModes()} underline>
            {t('reporting_mode_modal.learn_more')}
          </MyLink>
        </div>
      </div>
      <div className={s.content}>
        <div className={s.cards}>
          {modes.map(mode => (
            <div
              key={mode.reporting_mode}
              onClick={() => setSelected(mode)}
              className={classnames(
                s.card,
                status.reporting_mode === mode.reporting_mode && s.card_current,
                !mode.is_available && s.card_disabled,
                selected && selected.reporting_mode === mode.reporting_mode && s.card_selected,
              )}
            >
              <div className={s.left}>
                <div className={classnames(s.title, 'f-body-medium')}>
                  {REPORTING_MODES_INFO[mode.reporting_mode].title}{' '}
                  <span className={s.current}>{t('reporting_mode_modal.current')}</span>
                  {mode.reporting_mode === REPORTING_MODES.TRANSPARENT && isRevertTransparent && (
                    <span className={s.date}>
                      {t('reporting_mode_modal.starts_on', {
                        date: prettyDate(status.next_reporting_mode_from, 'MMMM DD, YYYY'),
                      })}
                    </span>
                  )}
                </div>
                <div className={classnames(s.desc, 'f-label')}>
                  {t('reporting_mode_modal.full_reporting_package', {
                    months: mode.period_months,
                  })}
                </div>
              </div>
              <div className={s.right}>
                <img
                  src={
                    selected?.reporting_mode === mode.reporting_mode ? IconChecked : IconUnchecked
                  }
                  alt="icon"
                  width={20}
                  height={20}
                />
              </div>
            </div>
          ))}
        </div>

        {modes && hasUnavailable && status.reporting_mode !== REPORTING_MODES.TRANSPARENT && (
          <div className={s.warning}>
            <div className={s.warning_icon}>
              <img src={IconWarn} alt="icon" width={24} height={24} />
            </div>
            <div className="'f-label'">{t('reporting_mode_modal.transparent_mode_warning')}</div>
          </div>
        )}

        {selected && (
          <div className={s.modes}>
            <div className={classnames(s.modes_title, 'f-h3 f-500')}>
              {t('reporting_mode_modal.mode_summary')}
            </div>
            <div className={s.modes_row}>
              <div className="f-body-regular">{t('reporting_mode_modal.reporting_frequency')}</div>
              <div className="f-body-medium">
                {t('reporting_mode_modal.every_months', { months: selected.period_months })}
              </div>
            </div>
            <div className={s.modes_row}>
              <div className={s.modes_row_label}>
                <div className="f-body-regular">{t('reporting_mode_modal.service_fee')}</div>
                <div className="f-label">{t('reporting_mode_modal.outstanding_balance')}</div>
              </div>
              <div className="f-body-medium">
                {selected.service_fee}%/{t('reporting_mode_modal.month')}
              </div>
            </div>
            <div className={s.modes_row}>
              <div className={s.modes_row_label}>
                <div className="f-body-regular">{t('reporting_mode_modal.non_compliance_fee')}</div>
                <div className="f-label">{t('reporting_mode_modal.miss_deadline')}</div>
              </div>
              <div className="f-body-medium">{selected.non_compliance_fee}%</div>
            </div>
          </div>
        )}
      </div>
      {isFirstTime ? (
        <div className={s.footer}>
          {selected && (
            <button
              type="button"
              className={classnames(r.black_large, s.btn_manage)}
              onClick={onSubmit}
            >
              {t('reporting_mode_modal.continue_with', {
                title: REPORTING_MODES_INFO[selected.reporting_mode].title,
              })}
            </button>
          )}
        </div>
      ) : (
        <div className={classnames(s.footer, !isFirstTime && s.footer_double)}>
          <button
            type="button"
            className={classnames(r.grey_large, s.btn_cancel)}
            onClick={onClose}
          >
            {t('reporting_mode_modal.cancel')}
          </button>
          {selected && (
            <button
              type="button"
              className={classnames(r.black_large, s.btn_apply)}
              onClick={onSubmit}
              disabled={
                selected?.reporting_mode === status.reporting_mode && !status.next_reporting_mode
              }
            >
              {isRevertTransparent && selected?.reporting_mode === status.reporting_mode
                ? t('reporting_mode_modal.keep_mode')
                : t('reporting_mode_modal.apply_mode')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <ModalCenterLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalCenterLayout>
  );
};
